import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Brain, Trash2, AlertTriangle, Activity } from "lucide-react";
import { toast } from "sonner";

/**
 * Ce a învățat Atena despre mine — vizibil și ștergibil.
 *
 * Fără ecranul ăsta nu e învățare, e drift: un sistem care își schimbă purtarea
 * pe baza unor reguli pe care nu le poți enumera nu a învățat nimic despre
 * tine, s-a mișcat, și tu nu știi încotro.
 */
export default function InvatareSettings({ workspaceId }) {
  const [date, setDate] = useState(null);
  const [evaluari, setEvaluari] = useState([]);
  const [corectie, setCorectie] = useState("");
  const [intrarea, setIntrarea] = useState("");
  const [busy, setBusy] = useState(false);
  const [eroare, setEroare] = useState("");

  const incarca = useCallback(async () => {
    try {
      const { data } = await api.get("/invatare/preferinte", { params: { workspace_id: workspaceId } });
      setDate(data);
      setEroare("");
    } catch (e) {
      setEroare(e?.response?.data?.detail || "Nu am putut citi preferințele");
    }
  }, [workspaceId]);

  useEffect(() => { if (workspaceId) incarca(); }, [workspaceId, incarca]);

  useEffect(() => {
    if (!workspaceId) return;
    api.get("/invatare/evaluari", { params: { workspace_id: workspaceId } })
      .then((r) => setEvaluari(r.data.rulari || []))
      .catch(() => setEvaluari([]));
  }, [workspaceId, date]);

  const invata = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invatare/corectie", {
        workspace_id: workspaceId, text: corectie,
        intrarea_care_a_gresit: intrarea,
      });
      setCorectie("");
      setIntrarea("");
      toast.success(data.caz_de_evaluare
        ? "Am reținut, și am adăugat un caz de verificare"
        : "Am reținut");
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut reține");
    } finally { setBusy(false); }
  };

  const sterge = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/invatare/preferinte/${id}`, { params: { workspace_id: workspaceId } });
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut șterge");
    } finally { setBusy(false); }
  };

  const evalueaza = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invatare/evaluare", null,
        { params: { workspace_id: workspaceId }, timeout: 300000 });
      toast.success(data.propozitie);
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluarea nu a putut rula");
    } finally { setBusy(false); }
  };

  if (!date && !eroare) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="invatare-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Brain className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Ce a învățat despre mine</h3>
        <span className="label-tiny ml-2">// VIZIBIL ȘI ȘTERGIBIL</span>
        <button type="button" data-testid="invatare-evalueaza" onClick={evalueaza}
                disabled={busy || !date?.cazuri_de_evaluare}
                className="ml-auto flex items-center gap-1 text-[11px] text-brand-2 hover:text-brand disabled:opacity-40">
          <Activity className="w-3 h-3" /> verifică acum
        </button>
      </div>

      <div className="p-5 space-y-4">
        {eroare && <p className="text-xs text-red-400" data-testid="invatare-eroare">{eroare}</p>}

        {date && (
          <>
            <p className="text-xs text-fg/50 leading-relaxed">
              Ce spui acum bate ce a învățat; ce a învățat bate implicitul din configurare.
              Între două reguli care se contrazic o folosește pe cea nouă — și îți spune.
            </p>

            {date.preferinte.length === 0 ? (
              <p className="text-xs text-fg/50" data-testid="invatare-goale">
                N-a învățat nimic încă. Corectează-o mai jos: „de acum înainte răspunde
                mai scurt".
              </p>
            ) : (
              <ul className="space-y-1" data-testid="invatare-lista">
                {date.preferinte.map((p) => {
                  const seAplica = date.aplicate.some((a) => a.text === p.regula);
                  return (
                    <li key={p.id} className="flex items-center gap-3 text-sm">
                      <span className="label-tiny w-20 flex-shrink-0">{p.subiect}</span>
                      <span className={seAplica ? "text-fg" : "text-fg/30 line-through"}>
                        {p.regula}
                      </span>
                      <button type="button" onClick={() => sterge(p.id)} disabled={busy}
                              data-testid={`invatare-sterge-${p.id}`}
                              aria-label={`Șterge regula ${p.regula}`}
                              className="ml-auto text-fg/30 hover:text-red-400 disabled:opacity-40">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {date.contradictii?.length > 0 && (
              <div className="text-xs text-amber-400 space-y-1" data-testid="invatare-contradictii">
                {date.contradictii.map((c, i) => (
                  <p key={i} className="flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{c}
                  </p>
                ))}
              </div>
            )}

            <div className="border-t border-line/10 pt-3 space-y-2">
              <label className="label-tiny block" htmlFor="invatare-corectie">
                corectează purtarea
              </label>
              <input id="invatare-corectie" data-testid="invatare-corectie"
                     className="input-flat text-sm w-full" value={corectie}
                     onChange={(e) => setCorectie(e.target.value)} />
              <label className="label-tiny block" htmlFor="invatare-intrarea">
                ce ai întrebat când a greșit (ca să pot verifica pe viitor)
              </label>
              <input id="invatare-intrarea" data-testid="invatare-intrarea"
                     className="input-flat text-sm w-full" value={intrarea}
                     onChange={(e) => setIntrarea(e.target.value)} />
              <button type="button" data-testid="invatare-trimite" onClick={invata}
                      disabled={busy || !corectie.trim()}
                      className="px-3 py-2 text-sm border border-brand/50 text-brand hover:bg-brand/10 disabled:opacity-40">
                Reține
              </button>
            </div>

            <div className="border-t border-line/10 pt-3" data-testid="invatare-evaluari">
              <p className="label-tiny mb-2">
                verificări ({date.cazuri_de_evaluare} cazuri în set)
              </p>
              {evaluari.length === 0 ? (
                <p className="text-xs text-fg/40" data-testid="invatare-fara-evaluari">
                  Nicio verificare încă. Setul crește din corecțiile tale.
                </p>
              ) : (
                <ul className="space-y-1">
                  {evaluari.slice(0, 8).map((r) => (
                    <li key={r.id} className="text-xs text-fg/60">
                      <span className="mono text-fg/40">{(r.la || "").slice(0, 16).replace("T", " ")}</span>{" "}
                      {r.propozitie}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
